import { Injectable, OnDestroy } from '@angular/core';
import { IReport, IReportTemplateArea, IReportTemplateSection, ISignature, ReportStatus } from '@shared/interfaces';
import { GuardService } from '../../../shared/services/guard.service';
import { BehaviorSubject, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ReportGuidanceComponent } from '../components/report-guidance/report-guidance.component';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../../current-project';
import { QuestionType } from '../../../shared/enums/QuestionType';
import { YesNoEnum } from '@shared/enums';
import { ExplanationDisplayType } from '../../../shared/enums/explanation-display-type';

@Injectable()
export class ReportBuilderService implements OnDestroy {
    guidanceRef: {
        dialog: MatDialogRef<ReportGuidanceComponent>;
        id: string;
    }[] = [];
    language = this.translateService.currentLang;
    rootSubscription: Subscription;
    activeSection = -1;
    burgerOpen = false;
    reportSubscribable = new BehaviorSubject<IReport>(null);
    totalValues: number;
    minPercentage = 0.9;

    previousReport: string;
    private _report: IReport;
    private destroy$ = new Subject<boolean>();

    constructor(
        private guard: GuardService,
        private translateService: TranslateService
    ) {
        this.listenToLangChange();
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    get report(): IReport {
        return this._report;
    }

    set report(report: IReport) {
        if (!report) {
            return;
        }

        this._report = report;

        this.calculateDoc();
        this.report.template.sections.forEach(section => {
            section.doc = this.getSectionFilledValues(section) / this.getSectionTotalValues(section);
        });

        this.reportSubscribable.next(report);
        this.previousReport = JSON.stringify(report);
    }

    get responsibleSignature(): ISignature {
        return this.report?.responsibleSignature;
    }

    get specialistSignature(): ISignature | ISignature[] {
        return this.report?.specialistSignature;
    }

    get isAbleToEditAsResponsible(): boolean {
        return this.report
            ? this.report.responsible?.responsible?.some(e => e.id === this.guard.currentUser?.id)
            : false;
    }

    get isAbleToSignAsResponsible(): boolean {
        return (
            this.isAbleToEditAsResponsible &&
            this.report.responsible?.responsible?.find(e => e.id === this.guard.currentUser?.id).fullAccess
        );
    }

    get isAbleToEditAsSpecialist(): boolean {
        return this.report
            ? this.report.responsible?.specialists?.some(e => e.id === this.guard.currentUser?.id)
            : false;
    }

    get isAbleToSignAsSpecialist(): boolean {
        return (
            this.isAbleToEditAsSpecialist &&
            this.report.responsible?.specialists?.find(e => e.id === this.guard.currentUser.id).fullAccess
        );
    }

    get isReleased(): boolean {
        return this.report ? this.report.reportStatus === ReportStatus.released : null;
    }

    get isReadyToSign(): boolean {
        return this.report.doc >= this.minPercentage;
    }

    get reportLanguage(): string {
        return this.report ? this.report.selectedLanguage : null;
    }

    /**
     * Calculates and updates the `doc` and `reportStatus` property for a given section and the entire report.
     *
     * This method performs the following:
     * - If a section is provided, it calculates the `doc` value for that section based on the ratio of filled values to total values.
     * - Calculates the overall `doc` value for the report, where:
     *   - 90% of the total `doc` value is derived from the filled fields in the report.
     *   - 10% of the total `doc` value is derived from the presence of signatures.
     *
     * If the calculated percentage is below the minimum required (`minPercentage`), the responsible and specialist signatures are reset.
     *
     * The final `doc` value is rounded to two decimal places.
     *
     * @param section Optional. The section of the report for which the `doc` value is calculated.
     */
    public calculateDoc(section?: IReportTemplateSection): void {
        if (section) {
            const filled = this.getSectionFilledValues(section);
            const total = this.getSectionTotalValues(section);
            section.doc = filled / total;
        }

        const filledValues = this.report.template.sections.reduce((sum, sec) => {
            return sum + this.getSectionFilledValues(sec);
        }, 0);
        const totalValues = this.report.template.sections.reduce((sum, sec) => {
            return sum + this.getSectionTotalValues(sec);
        }, 0);
        const valueCount = (filledValues / totalValues) * 0.9;

        let percentage = 0;
        if (IS_TRS_PROJECT) {
            const activeSpecialistIds =
                this.report.responsible.specialists?.filter(e => e.fullAccess).map(e => e.id) ?? [];
            const uniqueSignedSpecialistIds = new Set(
                (this.report.specialistSignature as ISignature[])
                    ?.filter(signature => activeSpecialistIds.includes(signature.id))
                    .map(signature => signature.id) ?? []
            );
            const signatureCount =
                activeSpecialistIds.length > 0
                    ? (uniqueSignedSpecialistIds.size / activeSpecialistIds.length) * 0.05
                    : 0;

            const responsibleSignatureCount = ((this.report.responsibleSignature?.date ? 1 : 0) / 1) * 0.05;
            percentage = valueCount + signatureCount + responsibleSignatureCount;

            if (percentage < this.minPercentage) {
                this.report.responsibleSignature = null;
                this.report.specialistSignature = [];
            }
        } else if (IS_PRODUCT_SAFETY_PROJECT) {
            const signatureCount =
                (((this.report.specialistSignature ? 1 : 0) + (this.report.responsibleSignature ? 1 : 0)) / 2) * 0.1;
            percentage = valueCount + signatureCount;

            if (percentage < this.minPercentage) {
                this.report.responsibleSignature = null;
                this.report.specialistSignature = null;
            }
        }

        this.report.doc = +percentage.toFixed(2);
        this.updateReportStatus();
    }

    public reset(): void {
        this.activeSection = -1;
        this.burgerOpen = false;
        this.report = null;
        this.totalValues = null;
    }

    private listenToLangChange(): void {
        this.translateService.onLangChange
            .pipe(
                takeUntil(this.destroy$),
                tap((event: LangChangeEvent) => {
                    this.language = event.lang;
                })
            )
            .subscribe();
    }

    /**
     * Returns the number of total values in a section.
     * @param section The section to check.
     * @returns The number of total values.
     */
    private getSectionTotalValues(section: IReportTemplateSection): number {
        return section.areas
            .filter(area => this.isAreaDependencySatisfied(section, area))
            .reduce((sum, area) => {
                switch (area.questionType) {
                    case QuestionType.value:
                    case QuestionType.text:
                        return sum + 2;
                    case QuestionType.yesNo: {
                        const valueId = area.rows[0][0].valueId;
                        const value = this.report.reportValues[valueId].value;
                        const answerDisplayType =
                            value === 'true'
                                ? ExplanationDisplayType.yes
                                : value === 'false'
                                  ? ExplanationDisplayType.no
                                  : null;
                        const answerMatches =
                            area.explanationDisplayType === ExplanationDisplayType.both ||
                            area.explanationDisplayType === answerDisplayType;
                        return sum + 2 + (area.isExplanationMandatory && answerMatches ? 1 : 0);
                    }
                    default:
                        return sum;
                }
            }, 0);
    }

    /**
     * Returns the number of filled values in a section.
     * @param section The section to check.
     * @returns The number of filled values.
     */
    private getSectionFilledValues(section: IReportTemplateSection): number {
        return section.areas
            .filter(area => this.isAreaDependencySatisfied(section, area))
            .reduce((sum, area) => {
                switch (area.questionType) {
                    case QuestionType.value:
                    case QuestionType.text: {
                        const textFieldId = area.rows[0][0].valueId;
                        const textFieldValue = this.report.reportValues[textFieldId].value;
                        const approved = this.report.reportValues[textFieldId].approved;
                        return sum + (textFieldValue ? 1 : 0) + (approved ? 1 : 0);
                    }
                    case QuestionType.yesNo: {
                        const radioFieldId = area.rows[0][0].valueId;
                        const radioFieldValue = this.report.reportValues[radioFieldId].value;
                        const answerDisplayType =
                            radioFieldValue === 'true'
                                ? ExplanationDisplayType.yes
                                : radioFieldValue === 'false'
                                  ? ExplanationDisplayType.no
                                  : null;
                        const answerMatches =
                            area.explanationDisplayType === ExplanationDisplayType.both ||
                            area.explanationDisplayType === answerDisplayType;
                        const approved = this.report.reportValues[radioFieldId].approved;
                        const explanationFieldId = area.rows[0][1].valueId;
                        const explanationFieldValue = this.report.reportValues[explanationFieldId].value;
                        return (
                            sum +
                            (radioFieldValue ? 1 : 0) +
                            (area.isExplanationMandatory && answerMatches && explanationFieldValue ? 1 : 0) +
                            (approved ? 1 : 0)
                        );
                    }
                    default:
                        return sum;
                }
            }, 0);
    }

    /**
     * Updates the report status based on the `doc` property.
     * - If `doc` is 0, the status is set to `ReportStatus.draft`.
     * - If `doc` is 1, the status is set to `ReportStatus.released`.
     * - Otherwise, the status is set to `ReportStatus.inProgress`.
     */
    private updateReportStatus(): void {
        switch (this.report.doc) {
            case 0:
                this.report.reportStatus = ReportStatus.draft;
                break;
            case 1:
                this.report.reportStatus = ReportStatus.released;
                break;
            default:
                this.report.reportStatus = ReportStatus.inProgress;
                break;
        }
    }

    /**
     * Checks if the area dependency is satisfied based on the report values.
     *
     * @param {IReportTemplateSection} section The section containing the area.
     * @param {IReportTemplateArea} area The area to check.
     * @returns True if the area dependency is satisfied, false otherwise.
     */
    private isAreaDependencySatisfied(section: IReportTemplateSection, area: IReportTemplateArea): boolean {
        if (area.dependsOn === -1) {
            return true; // No dependency, always satisfied
        }

        const dependantArea = section.areas[area.dependsOn - 1];
        const answer = this.report.reportValues[dependantArea.rows[0][0].valueId].value;
        const result = answer === 'true' ? YesNoEnum.Yes : answer === 'false' ? YesNoEnum.No : null;

        return area.dependsOnAnswer === result && this.isAreaDependencySatisfied(section, dependantArea);
    }
}
